import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import { Markup } from 'interweave';
import Spinner from '@strava/ui/Spinner';
import {
  InfiniteLoader,
  CellMeasurer,
  List,
  AutoSizer,
  CellMeasurerCache,
  WindowScroller
} from 'react-virtualized';
import styles from './feed.scss';

import Activity from './components/Activity';
import Post from './components/Post';
import FancyPromo from './components/FancyPromo';
import PerformanceGoalCreated from './components/PerformanceGoalCreated';
import ChallengeJoin from './components/ChallengeJoin';
import SimplePromo from './components/SimplePromo';
import GroupActivity from './components/GroupActivity';

import { feedTypeProps, pageProps } from './props/feed-prop-types';
import useFetchFeedEntries from './hooks/useFetchFeedEntries';

const Feed = ({
  currentAthleteId,
  page,
  feedType,
  athleteProfileId,
  clubId,
  useMockData,
  preFetchedEntries
}) => {
  const {
    isLoading,
    error,
    entries,
    pagination,
    loadEntries
  } = useFetchFeedEntries({
    currentAthleteId,
    clubId,
    feedType,
    page,
    useMockData,
    preFetchedEntries
  });

  // cache stores the row heights needed for virtualized scrolling
  const [cache] = useState(
    new CellMeasurerCache({ defaultHeight: 10, fixedWidth: true })
  );

  // listRef is needed to update the sizes of the feed cards
  // in the feed list if they change
  const [listRef, setListRef] = useState(null);

  // registers the react-virtualized container list containing feed card entries
  // so that it can be targeted for a refresh/ re-render
  const refCallback = useCallback((ref) => {
    if (ref) {
      setListRef(ref);
    }
  }, []);

  const { maxEntries, hasMore } = pagination;
  const i18nPrefix = 'components.feed';

  // return the proper feed card that can be rendered
  const renderEntry = (entryProps, index) => {
    const props = Object.assign(entryProps, {
      athleteProfileId, // for analytics - when viewing feed on athlete page
      clubId, // for analytics - when viewing feed on club details page
      index,
      page,
      feedType,
      key: index
    });
    switch (entryProps.entity) {
      case 'Activity':
        return <Activity {...props} />;
      case 'Post':
        return <Post {...props} />;
      case 'FancyPromo':
        return <FancyPromo {...props} />;
      case 'PerformanceGoalCreated':
        return <PerformanceGoalCreated {...props} />;
      case 'Challenge':
        return <ChallengeJoin {...props} />;
      case 'SimplePromo':
        return <SimplePromo {...props} />;
      case 'GroupActivity':
        return <GroupActivity {...props} />;
      default:
        return <div>N/A RF</div>; // TODO: Replace this with sentry error logging in monorepo
    }
  };

  // TODO: implement me!
  // eslint-disable-next-line no-unused-vars
  const endMessage = () => {
    if (page === 'dashboard' && !isLoading) {
      const content = maxEntries
        ? `${i18nPrefix}.no_more.limit`
        : `${i18nPrefix}.no_more.days`;
      return (
        <div className={`${styles.feedPagination} ${styles.noEntries}`}>
          {I18n.t(content)}
          <br />
          <Markup
            noWrap={true}
            content={I18n.t(`${i18nPrefix}.no_more.full_html`, {
              athlete_id: currentAthleteId
            })}
          />
        </div>
      );
    }
    return null;
  };

  // TODO: implement me!
  // eslint-disable-next-line no-unused-vars
  const loader = () => {
    return (
      <div className={styles.feedPagination}>
        <div className={styles.spinner}>
          {error ? I18n.t(`${i18nPrefix}.error_message`) : <Spinner />}
        </div>
      </div>
    );
  };

  // Invoked when more rows must be loaded.
  // Note: This function may be called multiple times in response to a single scroll event.
  // See README for details on how pagination works on the feed
  async function loadMoreRows() {
    let minBeforeEntry;
    let minRankEntry;
    if (entries.length > 0) {
      minBeforeEntry = entries.reduce((result, entry) =>
        entry.cursorData.updated_at < result.cursorData.updated_at
          ? entry
          : result
      );
      minRankEntry = entries.reduce((result, entry) =>
        entry.cursorData.rank < result.cursorData.rank ? entry : result
      );
    }
    await loadEntries({
      cursorData: {
        before: minBeforeEntry ? minBeforeEntry.cursorData.updated_at : null,
        rank: minRankEntry ? minRankEntry.cursorData.rank : null
      }
    });
  }

  // Number of card entries on the feed
  // If there are more items to be loaded, we add an extra row to hold a
  // loading indicator / spinner.
  const rowCount = (() => (hasMore ? entries.length + 1 : entries.length))();

  // responsible for tracking the loaded state of each row.
  const isRowLoaded = ({ index }) => {
    return !hasMore || index < entries.length;
  };

  // use this to update the row sizing when card sizes change
  // for example opening the comments on a post
  // TODO - implement me
  // eslint-disable-next-line no-unused-vars
  const recomputeAll = () => {
    cache.clearAll();
    if (listRef) {
      listRef.forceUpdateGrid();
    }
  };

  // responsible for rendering a single card entry (row), given its index
  const rowRenderer = (params) => {
    // [ named parameters from react-virtualized ]
    // index - index of row
    // key - unique key within array of rows
    // style - style object to be applied to row
    // parent - reference to the parents list
    const { index, key, style, parent } = params;
    if (!isRowLoaded({ index })) {
      return null; // feed card has already been rendered
    }
    return (
      <div key={key} style={style}>
        {/* automatically measures a cells content by temporarily rendering it
        in a way that is not visible to the user */}
        <CellMeasurer
          cache={cache}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          {renderEntry(entries[index], index)}
        </CellMeasurer>
      </div>
    );
  };

  return (
    <div className={styles.virtualizedFeed}>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={rowCount}
      >
        {({ onRowsRendered }) => (
          <WindowScroller>
            {({ height, isScrolling, onChildScroll, scrollTop }) => (
              <AutoSizer disableHeight={true}>
                {({ width }) => (
                  <List
                    autoHeight={true}
                    deferredMeasurementCache={cache}
                    height={height}
                    onRowsRendered={onRowsRendered}
                    ref={(ref) => refCallback(ref)}
                    rowCount={rowCount}
                    rowHeight={cache.rowHeight}
                    rowRenderer={rowRenderer}
                    width={width}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    scrollTop={scrollTop}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteLoader>
    </div>
  );
};

Feed.propTypes = {
  currentAthleteId: PropTypes.number.isRequired,
  page: pageProps.isRequired,
  feedType: feedTypeProps.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  clubId: PropTypes.string.isRequired,
  useMockData: PropTypes.bool.isRequired,
  preFetchedEntries: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};
export default Feed;
